import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading, Text, Button, Flex, Loader, Dropdown } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import {
  fetchLeaveReport,
  fetchWorklogReport,
  type ReportConfig,
  type ReportPeriod,
  type ReportScope,
  type PlannedVsActualReportResponse,
} from '../../api/report.api';
import {
  collectMappedColumnIds,
  fetchMondayUsers,
  fetchBoardsByIds,
  fetchFilteredBoardItems,
  fetchSubitemsBoardInfo,
  type MondayUser,
} from '../../api/monday.api';
import { useScheduleStore } from '../../stores/scheduleStore';
import {
  getBoardConfigsBulk,
  listBoardConfigs,
  getFieldMappingLocal,
  getSubitemFieldMappingLocal,
  getItemLevelLocal,
} from '../../api/board-config.api';
import { useCurrentUserAccess } from '../../hooks/useCurrentUserAccess';
import { getMyViewableTargets } from '../../api/permission.api';
import { resolveAllowedUserIds } from '../../utils/timesheet-permissions';
import { useMonday } from '../../hooks/useMonday';
import MultiBoardPicker from '../shared/MultiBoardPicker';
import type { PickedBoard } from '../shared/BoardPicker';
import {
  resolvePeriod,
  buildUtilizationReport,
  buildBoardResourcesReport,
} from '../../utils/report-calculation';
import { fetchWorkloadData } from '../../services/workload-data.service';
import { getWorklogSummaryByItemAndUser } from '../../api/worklog.api';
import {
  extractItemData,
  computePlannedVsActual,
  groupByBoard,
  aggregateByBoard,
  computeGrandTotal,
  type ExtractedItem,
} from '../../utils/planned-vs-actual';
import { sanitize } from '../../utils/sanitize';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Period options ──────────────────────────────────────────────────────────

const PERIOD_OPTIONS: Array<{ value: ReportPeriod; label: string }> = [
  { value: 'current_and_next_4_weeks', label: 'Current & Next 4 Weeks' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'next_quarter', label: 'Next Quarter' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'next_month', label: 'Next Month' },
  { value: 'next_2_weeks', label: 'Next 2 Weeks' },
  { value: 'current_week', label: 'Current Week' },
  { value: 'next_4_weeks', label: 'Next 4 Weeks' },
  { value: 'next_8_weeks', label: 'Next 8 Weeks' },
  { value: 'next_12_weeks', label: 'Next 12 Weeks' },
  { value: 'custom', label: 'Custom Range' },
];

const LEAVE_PERIOD_OPTIONS: Array<{ value: ReportPeriod; label: string }> = [
  { value: 'current_year', label: 'Current Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'next_year', label: 'Next Year' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'next_quarter', label: 'Next Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'custom', label: 'Custom Range' },
];

const WORKLOG_PERIOD_OPTIONS: Array<{ value: ReportPeriod; label: string }> = [
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'current_week', label: 'Current Week' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'last_quarter', label: 'Previous Quarter' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

const PLANNED_VS_ACTUAL_PERIOD_OPTIONS: Array<{ value: ReportPeriod; label: string }> = [
  { value: 'current_week', label: 'Current Week' },
  { value: 'previous_week', label: 'Previous Week' },
  { value: 'previous_2_weeks', label: 'Previous 2 Weeks' },
  { value: 'previous_4_weeks', label: 'Previous 4 Weeks' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'last_quarter', label: 'Previous Quarter' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

const SCOPE_OPTIONS: Array<{ value: ReportScope; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const GROUP_BY_OPTIONS: Array<{
  value: 'none' | 'board' | 'item' | 'team' | 'category';
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'team', label: 'Team' },
  { value: 'board', label: 'Board' },
  { value: 'item', label: 'Item' },
  { value: 'category', label: 'Category' },
];

// Worklog report supports a different subset — no team grouping (team dropdown
// is a filter for worklog reports, not a grouper).
const WORKLOG_GROUP_BY_OPTIONS: Array<{
  value: 'none' | 'board' | 'item' | 'category';
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'board', label: 'Board' },
  { value: 'item', label: 'Item' },
  { value: 'category', label: 'Category' },
];


/** Maximum number of configured boards included when no specific boards are selected */
const MAX_REPORT_BOARDS = 100;

// ─── Component ───────────────────────────────────────────────────────────────

const ReportConfigDialog: React.FC = () => {
  const {
    currentConfig,
    currentReportType,
    reportError,
    setCurrentConfig,
    setView,
    setReportData,
    setReportLoading,
    setReportError,
  } = useReportStore();

  const firstDayOfWeek = useScheduleStore((s) => s.firstDayOfWeek);

  const [users, setUsers] = useState<MondayUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Track selected boards for display in MultiBoardPicker
  const [selectedBoardsPicked, setSelectedBoardsPicked] = useState<PickedBoard[]>([]);

  // Sync selectedBoardsPicked when currentConfig.boardIds changes (e.g. reconfigure)
  useEffect(() => {
    const boardIds = currentConfig?.boardIds ?? [];
    // Skip if no board IDs or if already in sync
    const currentIds = new Set(selectedBoardsPicked.map((b) => b.id));
    const configIds = new Set(boardIds);
    const isSynced =
      currentIds.size === configIds.size &&
      boardIds.every((id) => currentIds.has(id));
    if (isSynced) return;

    if (boardIds.length === 0) {
      setSelectedBoardsPicked([]);
      return;
    }

    let cancelled = false;
    fetchBoardsByIds(boardIds).then((boards) => {
      if (!cancelled) {
        setSelectedBoardsPicked(
          boards.map((b) => ({ id: String(b.id), name: b.name })),
        );
      }
    }).catch((err) => {
      console.error('Failed to resolve board names for report config:', err);
    });
    return () => { cancelled = true; };
  }, [currentConfig?.boardIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch users on mount
  useEffect(() => {
    let cancelled = false;
    fetchMondayUsers()
      .then((u) => {
        if (!cancelled) setUsers(u);
      })
      .catch((err) => console.error('Failed to load users:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived: unique teams from users
  const teams = useMemo(() => {
    const teamMap = new Map<number, string>();
    for (const user of users) {
      for (const team of user.teams) {
        teamMap.set(team.id, team.name);
      }
    }
    return Array.from(teamMap.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  // ─── Permission-aware filtering for worklog report ─────────────────────────
  const { isAdmin, isLoading: isAccessLoading } = useCurrentUserAccess();
  const { context } = useMonday();
  // undefined = loading, null = no filter (admin/viewAll), number[] = allowed IDs
  const [allowedUserIds, setAllowedUserIds] = useState<number[] | null | undefined>(undefined);

  useEffect(() => {
    if (isAccessLoading || users.length === 0) return;
    if (isAdmin) {
      setAllowedUserIds(null);
      return;
    }
    const currentId = context?.userId ? Number(context.userId) : null;
    getMyViewableTargets()
      .then((resp) => {
        setAllowedUserIds(resolveAllowedUserIds(resp, currentId, users));
      })
      .catch(() => {
        setAllowedUserIds(currentId ? [currentId] : []);
      });
  }, [isAccessLoading, isAdmin, users, context?.userId]);

  const isWorklogReportType = currentReportType === 'detailed_worklog';

  const updateConfig = useCallback(
    (updates: Partial<ReportConfig>) => {
      if (currentConfig) {
        setCurrentConfig({ ...currentConfig, ...updates });
      }
    },
    [currentConfig, setCurrentConfig],
  );

  const handleGenerate = useCallback(async () => {
    if (!currentConfig || generating) return;

    const isBoardResources = currentReportType === 'board_resources_forecast';
    const isLeaveReport = currentReportType === 'leave_report';
    const isWorklogReport = currentReportType === 'detailed_worklog';
    const isPlannedVsActual = currentReportType === 'planned_vs_actual';

    setGenerating(true);
    setReportLoading(true);
    setReportError(null);

    try {
      // 1. Resolve period to date range
      const { startDate, endDate } = resolvePeriod(
        currentConfig.period,
        currentConfig.customStartDate,
        currentConfig.customEndDate,
        firstDayOfWeek,
      );

      if (isPlannedVsActual) {
        const boardIds = currentConfig.boardIds ?? [];
        if (boardIds.length === 0) {
          throw new Error('Please select at least one board');
        }

        // Load all board configs in a single bulk request (instead of N individual calls)
        await getBoardConfigsBulk(boardIds.map(Number));

        // Fetch items from all boards with bounded parallelism. Per-request
        // complexity is small (page limit 100 + column filtering), so 8
        // concurrent requests stay safely under monday.com's limits.
        const allExtracted: ExtractedItem[] = [];
        const MAX_CONCURRENT = 8;

        for (let i = 0; i < boardIds.length; i += MAX_CONCURRENT) {
          const batch = boardIds.slice(i, i + MAX_CONCURRENT);
          await Promise.all(
            batch.map(async (boardIdStr) => {
              const boardId = Number(boardIdStr);
              const fieldMapping = getFieldMappingLocal(boardId);
              if (!fieldMapping) return;

              const itemLevel = getItemLevelLocal(boardId);

              // Build timeline filter if the board has a timeline column mapped
              const timelineFilter = fieldMapping.timelineColumnId
                ? { timeline: { columnId: fieldMapping.timelineColumnId, startDate, endDate } }
                : {};

              // Fetch board name (needed for both main and sub items)
              let resolvedBoardName = '';
              if (itemLevel === 'main' || itemLevel === 'both') {
                // includeSubitems: false — subitems come from the separate
                // subitems-board fetch below, so the nested field is dead weight.
                // columnIds: only the mapped columns extractItemData actually reads.
                const mainColumnIds = collectMappedColumnIds(fieldMapping);
                const { boardName, items } = await fetchFilteredBoardItems(
                  boardId,
                  timelineFilter,
                  undefined,
                  false,
                  mainColumnIds,
                );
                resolvedBoardName = boardName;
                for (const item of items) {
                  allExtracted.push(
                    extractItemData(item, fieldMapping, boardIdStr, boardName, false),
                  );
                }
              }

              if (itemLevel === 'sub' || itemLevel === 'both') {
                const subInfo = await fetchSubitemsBoardInfo(boardId);
                if (subInfo) {
                  const subFieldMapping = getSubitemFieldMappingLocal(boardId);
                  if (subFieldMapping) {
                    const subTimelineFilter = subFieldMapping.timelineColumnId
                      ? { timeline: { columnId: subFieldMapping.timelineColumnId, startDate, endDate } }
                      : {};
                    const subColumnIds = collectMappedColumnIds(subFieldMapping);
                    const { boardName: subBoardName, items: subItems } = await fetchFilteredBoardItems(
                      subInfo.subitemsBoardId,
                      subTimelineFilter,
                      undefined,
                      false,
                      subColumnIds,
                    );
                    // In sub-only mode, resolve parent board name via lightweight query
                    if (!resolvedBoardName) {
                      try {
                        const resp = await monday.api(
                          `query ($ids: [ID!]!) { boards(ids: $ids) { name } }`,
                          { variables: { ids: [String(boardId)] } },
                        );
                        resolvedBoardName = (resp.data as { boards?: Array<{ name: string }> })?.boards?.[0]?.name ?? boardIdStr;
                      } catch {
                        resolvedBoardName = boardIdStr;
                      }
                    }
                    for (const item of subItems) {
                      allExtracted.push(
                        extractItemData(item, subFieldMapping, boardIdStr, resolvedBoardName, true),
                      );
                    }
                  }
                }
              }
            }),
          );
        }

        // Resolve team + people filters into a combined user ID list
        const hasTeamFilter = currentConfig.teamIds && currentConfig.teamIds.length > 0;
        const hasUserFilter = currentConfig.userIds && currentConfig.userIds.length > 0;
        let userIds: string[] | undefined;

        if (hasTeamFilter || hasUserFilter) {
          const userIdSet = new Set<string>();
          if (hasTeamFilter) {
            const teamIdSet = new Set(currentConfig.teamIds);
            for (const u of users) {
              if (u.teams.some((t) => teamIdSet.has(String(t.id)))) {
                userIdSet.add(String(u.id));
              }
            }
          }
          if (hasUserFilter) {
            for (const uid of currentConfig.userIds!) {
              userIdSet.add(uid);
            }
          }
          userIds = Array.from(userIdSet);
        }

        let filteredItems = allExtracted;
        if (userIds) {
          const userNameMap = new Map(users.map((u) => [String(u.id), u.name]));
          const filterNames = new Set(userIds.map((id) => userNameMap.get(id)).filter(Boolean));
          filteredItems = allExtracted.filter((item) => {
            if (!item.assignees) return false;
            const itemAssignees = item.assignees.split(',').map((n) => n.trim());
            return itemAssignees.some((name) => filterNames.has(name));
          });
        }

        // Fetch ALL-TIME worklog summaries per item (not date-filtered).
        // Remaining estimate = original - ALL worklogs, not just this period's worklogs.
        const rawSummaries = await getWorklogSummaryByItemAndUser(boardIds, userIds);
        // Aggregate per-user summaries to per-item totals (matching WorklogSummaryItem shape)
        const itemTotals = new Map<string, number>();
        for (const s of rawSummaries) {
          itemTotals.set(s.mondayItemId, (itemTotals.get(s.mondayItemId) ?? 0) + s.totalHours);
        }
        const summaries = Array.from(itemTotals.entries()).map(([mondayItemId, totalHours]) => ({
          mondayItemId,
          mondayBoardId: '',
          totalHours,
        }));

        // Compute report data
        const itemRows = computePlannedVsActual(filteredItems, summaries);

        // Sanitize item/board names (XSS prevention)
        for (const row of itemRows) {
          row.itemName = sanitize(row.itemName);
          row.boardName = sanitize(row.boardName);
          row.assignees = sanitize(row.assignees);
        }

        const boardGroups = groupByBoard(itemRows);
        const boardRows = aggregateByBoard(itemRows);
        const grandTotal = computeGrandTotal(itemRows);

        const result: PlannedVsActualReportResponse = {
          reportType: 'planned_vs_actual',
          generatedAt: new Date().toISOString(),
          period: { startDate, endDate },
          boardGroups,
          boardRows,
          grandTotal,
        };

        setReportData(result);
        setView('table');
        return;
      }

      if (isWorklogReport) {
        // Detailed Worklog Report: backend-driven
        const hasTeamFilter = currentConfig.teamIds && currentConfig.teamIds.length > 0;
        const hasUserFilter = currentConfig.userIds && currentConfig.userIds.length > 0;
        const allowedSet = Array.isArray(allowedUserIds)
          ? new Set(allowedUserIds.map(String))
          : null;
        let targetUserIds: string[] | undefined;

        if (hasTeamFilter || hasUserFilter) {
          const userIdSet = new Set<string>();

          if (hasTeamFilter) {
            const teamIdSet = new Set(currentConfig.teamIds);
            for (const u of users) {
              if (u.teams.some((t) => teamIdSet.has(String(t.id)))) {
                userIdSet.add(String(u.id));
              }
            }
          }

          if (hasUserFilter) {
            for (const uid of currentConfig.userIds!) {
              userIdSet.add(uid);
            }
          }

          // Intersect with allowed user IDs for non-admins
          if (allowedSet) {
            for (const uid of userIdSet) {
              if (!allowedSet.has(uid)) userIdSet.delete(uid);
            }
          }

          targetUserIds = Array.from(userIdSet);
        }

        const reportData = await fetchWorklogReport({
          startDate,
          endDate,
          userIds: targetUserIds,
          teamIds: currentConfig.teamIds,
        });

        // Enrich user names and board/item names from monday.com data
        const userNameMap = new Map(
          users.map((u) => [String(u.id), u.name]),
        );
        for (const group of reportData.groups) {
          for (const row of group.rows) {
            const name = userNameMap.get(row.userId);
            if (name) row.userName = name;
            const approverName = row.approverUserId
              ? userNameMap.get(row.approverUserId)
              : null;
            if (approverName) row.approverName = approverName;
          }
        }

        // Apply client-side grouping based on the groupBy config.
        // (Backend always returns a flat "All Worklogs" group — grouping is a
        // pure view concern for the worklog report.)
        const allRows = reportData.groups.flatMap((g) => g.rows);
        // Legacy configs may carry groupBy='team' — worklog report no longer
        // supports team grouping, so treat it as 'none'.
        const rawGroupBy = currentConfig.groupBy;
        const worklogGroupBy: 'none' | 'board' | 'item' | 'category' =
          rawGroupBy === 'board' || rawGroupBy === 'item' || rawGroupBy === 'category'
            ? rawGroupBy
            : 'none';

        if (worklogGroupBy === 'none') {
          reportData.groups = [
            { groupId: 'all', groupName: 'All Worklogs', rows: allRows },
          ];
        } else if (worklogGroupBy === 'board') {
          const map = new Map<string, typeof allRows>();
          for (const row of allRows) {
            const key = row.boardName || 'Unknown Board';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(row);
          }
          reportData.groups = [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([name, rows]) => ({
              groupId: `board:${name}`,
              groupName: name,
              rows,
            }));
        } else if (worklogGroupBy === 'item') {
          const map = new Map<string, typeof allRows>();
          for (const row of allRows) {
            const key = `${row.boardName || 'Unknown Board'} › ${row.itemName || 'Unknown Item'}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(row);
          }
          reportData.groups = [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([name, rows]) => ({
              groupId: `item:${name}`,
              groupName: name,
              rows,
            }));
        } else if (worklogGroupBy === 'category') {
          const map = new Map<string, typeof allRows>();
          for (const row of allRows) {
            const key = row.categoryName ?? 'Uncategorized';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(row);
          }
          reportData.groups = [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([name, rows]) => ({
              groupId: `category:${name}`,
              groupName: name,
              rows,
            }));
        }

        setReportData(reportData);
        setView('table');
        return;
      }

      if (isLeaveReport) {
        // Leave Report: entirely backend-driven
        const hasTeamFilter = currentConfig.teamIds && currentConfig.teamIds.length > 0;
        const hasUserFilter = currentConfig.userIds && currentConfig.userIds.length > 0;
        let targetUserIds: string[] | undefined;

        if (hasTeamFilter || hasUserFilter) {
          const userIdSet = new Set<string>();

          if (hasTeamFilter) {
            const teamIdSet = new Set(currentConfig.teamIds);
            for (const u of users) {
              if (u.teams.some((t) => teamIdSet.has(String(t.id)))) {
                userIdSet.add(String(u.id));
              }
            }
          }

          if (hasUserFilter) {
            for (const uid of currentConfig.userIds!) {
              userIdSet.add(uid);
            }
          }

          targetUserIds = Array.from(userIdSet);
        }

        const reportData = await fetchLeaveReport({
          startDate,
          endDate,
          userIds: targetUserIds,
          teamIds: currentConfig.teamIds,
          scope: currentConfig.scope ?? 'month',
          firstDayOfWeek,
        });

        // Enrich user names from monday.com data
        const userNameMap = new Map(
          users.map((u) => [String(u.id), { name: u.name, photo: u.photo_thumb_small }]),
        );
        for (const group of reportData.groups) {
          for (const row of group.rows) {
            const meta = userNameMap.get(row.userId);
            if (meta) {
              row.userName = meta.name;
              row.userPhoto = meta.photo;
            }
          }
        }

        // Re-group by team if teams are selected
        if (hasTeamFilter && currentConfig.teamIds!.length > 0) {
          const teamNameMap = new Map<string, string>();
          for (const u of users) {
            for (const t of u.teams) {
              teamNameMap.set(String(t.id), t.name);
            }
          }

          const groupMap = new Map<string, typeof reportData.groups[0]['rows']>();
          const allRows = reportData.groups.flatMap((g) => g.rows);

          for (const row of allRows) {
            const user = users.find((u) => String(u.id) === row.userId);
            const userTeamIds = user?.teams.map((t) => String(t.id)) ?? [];
            const relevantTeams = userTeamIds.filter((tid) =>
              currentConfig.teamIds!.includes(tid),
            );

            if (relevantTeams.length === 0) {
              const key = 'unassigned';
              if (!groupMap.has(key)) groupMap.set(key, []);
              groupMap.get(key)!.push(row);
            } else {
              for (const tid of relevantTeams) {
                if (!groupMap.has(tid)) groupMap.set(tid, []);
                groupMap.get(tid)!.push(row);
              }
            }
          }

          reportData.groups = Array.from(groupMap.entries()).map(([gid, rows]) => ({
            groupId: gid,
            groupName: teamNameMap.get(gid) ?? 'Unassigned',
            rows: rows.sort((a, b) => a.userName.localeCompare(b.userName)),
          }));
        } else {
          // Sort alphabetically in single group
          for (const group of reportData.groups) {
            group.rows.sort((a, b) => a.userName.localeCompare(b.userName));
          }
        }

        setReportData(reportData);
        setView('table');
        return;
      }

      // 2. Resolve which users to include
      // For board resources report: include all users (board-centric, no user filter)
      // For utilization report: teams and people selections are combined (union)
      let targetUserIds: string[];

      if (isBoardResources) {
        targetUserIds = users.map((u) => String(u.id));
      } else {
        const hasTeamFilter = currentConfig.teamIds && currentConfig.teamIds.length > 0;
        const hasUserFilter = currentConfig.userIds && currentConfig.userIds.length > 0;

        if (!hasTeamFilter && !hasUserFilter) {
          targetUserIds = users.map((u) => String(u.id));
        } else {
          const userIdSet = new Set<string>();

          if (hasTeamFilter) {
            const teamIdSet = new Set(currentConfig.teamIds);
            for (const u of users) {
              if (u.teams.some((t) => teamIdSet.has(String(t.id)))) {
                userIdSet.add(String(u.id));
              }
            }
          }

          if (hasUserFilter) {
            for (const uid of currentConfig.userIds!) {
              userIdSet.add(uid);
            }
          }

          targetUserIds = Array.from(userIdSet);
        }
      }

      if (targetUserIds.length === 0) {
        throw new Error('No users selected for the report');
      }

      // 3. Enforce future-only dates (past utilization/forecast is meaningless)
      const boardIds = currentConfig.boardIds ?? [];
      const nowLocal = new Date();
      const todayStr = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, '0')}-${String(nowLocal.getDate()).padStart(2, '0')}`;
      const effectiveStartDate = startDate < todayStr ? todayStr : startDate;

      // 4. Fetch worklogs for remaining estimate (same logic as scheduler).
      // When no specific boards are selected ("all boards" mode), resolve
      // configured board IDs first — otherwise worklogs won't be fetched
      // and the report silently falls back to original estimates.
      let worklogBoardIds = boardIds;
      if (worklogBoardIds.length === 0) {
        const configResp = await listBoardConfigs(1, 150);
        worklogBoardIds = configResp.items.map((c) => String(c.boardId));
      }

      let worklogsByItem: Map<string, number> | undefined;
      if (worklogBoardIds.length > 0) {
        try {
          const summaries = await getWorklogSummaryByItemAndUser(
            worklogBoardIds,
            targetUserIds,
          );
          worklogsByItem = new Map<string, number>();
          for (const s of summaries) {
            worklogsByItem.set(s.mondayItemId, (worklogsByItem.get(s.mondayItemId) ?? 0) + s.totalHours);
          }
        } catch {
          // If worklogs fail, fall back to original estimates
        }
      }

      // 5. Fetch workload data using remaining estimate mode (same as scheduler):
      //    past days → 0h, future days → remaining estimate distributed evenly
      const workloadResult = await fetchWorkloadData({
        startDate: effectiveStartDate,
        endDate,
        userIds: targetUserIds,
        boardIds: boardIds.length > 0 ? boardIds : undefined,
        worklogsByItem,
      });

      // Convert UserDayEntry[] to ScheduledWorkEntry[] for report builders.
      // Hours already reflect remaining estimate from computeSchedulerWorkload.
      const scheduledWork = workloadResult.entries.flatMap((entry) =>
        entry.items.map((item) => ({
          userId: entry.userId,
          boardId: item.boardId,
          boardName: item.boardName,
          itemId: item.itemId,
          itemName: item.itemName,
          date: entry.date,
          hours: item.hours,
          totalItemHours: item.totalItemHours,
          workHourFormat: item.workHourFormat,
          assigneeCount: item.assigneeCount,
        })),
      );

      if (isBoardResources) {
        const reportData = buildBoardResourcesReport(
          scheduledWork,
          users,
          effectiveStartDate,
          endDate,
          currentConfig.scope ?? 'week',
          firstDayOfWeek,
        );
        setReportData(reportData);
      } else {
        // Convert UserDayEntry[] to AvailabilityResponse for buildUtilizationReport
        const userAvailMap = new Map<
          string,
          { days: import('../../utils/report-calculation').DailyAvailability[]; total: number }
        >();
        for (const entry of workloadResult.entries) {
          let record = userAvailMap.get(entry.userId);
          if (!record) {
            record = { days: [], total: 0 };
            userAvailMap.set(entry.userId, record);
          }
          record.days.push({
            date: entry.date,
            capacityHours: entry.capacityHours,
            holidayHours: entry.holidayHours,
            leaveHours: entry.leaveHours,
            availableHours: entry.availableHours,
            isHoliday: entry.isHoliday,
            isLeave: entry.isLeave,
            isWeekend: entry.isWeekend,
          });
          record.total += entry.availableHours;
        }

        const availability = {
          startDate,
          endDate,
          users: Array.from(userAvailMap.entries()).map(([userId, record]) => ({
            userId,
            totalAvailableHours: Math.round(record.total * 100) / 100,
            days: record.days,
          })),
        };

        const reportData = buildUtilizationReport(
          availability,
          scheduledWork,
          currentConfig,
          users,
          firstDayOfWeek,
        );
        setReportData(reportData);
      }

      setView('table');
    } catch (err) {
      setReportError((err as Error).message);
    } finally {
      setGenerating(false);
      setReportLoading(false);
    }
  }, [currentConfig, currentReportType, users, generating, setReportData, setReportLoading, setReportError, setView]);

  const handleCancel = useCallback(() => {
    setCurrentConfig(null);
    setView('gallery');
  }, [setCurrentConfig, setView]);

  // ── Dropdown option arrays (must be before early returns — Rules of Hooks) ─

  const teamOptions = useMemo(() => {
    const opts = teams.map((t) => ({ value: String(t.id), label: t.name }));
    if (!isWorklogReportType || !Array.isArray(allowedUserIds)) return opts;
    // For worklog report, only show teams that have at least one allowed member
    const allowedSet = new Set(allowedUserIds);
    return opts.filter((t) => {
      const teamId = Number(t.value);
      return users.some((u) => u.teams.some((tm) => tm.id === teamId) && allowedSet.has(u.id));
    });
  }, [teams, isWorklogReportType, allowedUserIds, users]);

  const userOptions = useMemo(() => {
    const opts = users.map((u) => ({ value: String(u.id), label: u.name }));
    if (!isWorklogReportType || !Array.isArray(allowedUserIds)) return opts;
    const allowedSet = new Set(allowedUserIds.map(String));
    return opts.filter((o) => allowedSet.has(o.value));
  }, [users, isWorklogReportType, allowedUserIds]);

  // ── Selected option values (for controlled Dropdown) ──────────────────────

  const selectedTeamValues = useMemo(
    () => teamOptions.filter((opt) => (currentConfig?.teamIds ?? []).includes(opt.value)),
    [teamOptions, currentConfig?.teamIds],
  );

  const selectedUserValues = useMemo(
    () => userOptions.filter((opt) => (currentConfig?.userIds ?? []).includes(opt.value)),
    [userOptions, currentConfig?.userIds],
  );

  const selectedPeriodValue = useMemo(() => {
    const opts = currentReportType === 'leave_report'
      ? LEAVE_PERIOD_OPTIONS
      : currentReportType === 'detailed_worklog'
        ? WORKLOG_PERIOD_OPTIONS
        : PERIOD_OPTIONS;
    return opts.find((opt) => opt.value === currentConfig?.period) ?? opts[0];
  }, [currentConfig?.period, currentReportType]);

  const selectedScopeValue = useMemo(
    () => SCOPE_OPTIONS.find((opt) => opt.value === currentConfig?.scope) ?? SCOPE_OPTIONS[0],
    [currentConfig?.scope],
  );

  const groupByOptions = useMemo(
    () =>
      currentReportType === 'detailed_worklog'
        ? WORKLOG_GROUP_BY_OPTIONS
        : GROUP_BY_OPTIONS,
    [currentReportType],
  );

  const selectedGroupByValue = useMemo(
    () => groupByOptions.find((opt) => opt.value === currentConfig?.groupBy) ?? groupByOptions[0],
    [groupByOptions, currentConfig?.groupBy],
  );

  // ── Derived: report type flags ──────────────────────────────────────────

  const isBoardResources = currentReportType === 'board_resources_forecast';
  const isLeaveReport = currentReportType === 'leave_report';
  const isWorklogReport = currentReportType === 'detailed_worklog';
  const isPlannedVsActual = currentReportType === 'planned_vs_actual';

  const dialogTitle = isPlannedVsActual
    ? 'Planned vs Actual'
    : isWorklogReport
      ? 'Detailed Worklog Report'
      : isLeaveReport
        ? 'Leave Report'
        : isBoardResources
          ? 'Board Resources Forecast'
          : 'Resource Utilization Forecast';

  const activePeriodOptions = isPlannedVsActual
    ? PLANNED_VS_ACTUAL_PERIOD_OPTIONS
    : isWorklogReport
      ? WORKLOG_PERIOD_OPTIONS
      : isLeaveReport
        ? LEAVE_PERIOD_OPTIONS
        : PERIOD_OPTIONS;

  // ── Early returns (after all hooks) ───────────────────────────────────────

  if (!currentConfig) return null;

  if (loading) {
    return (
      <Flex justify={Flex.justify.CENTER} style={{ paddingTop: 48 }}>
        <Loader size={Loader.sizes.MEDIUM} />
      </Flex>
    );
  }

  return (
    <div className="rp-report-config">
      <div className="rp-report-config__dialog">
        <Flex
          justify={Flex.justify.SPACE_BETWEEN}
          align={Flex.align.CENTER}
          style={{ marginBottom: 24 }}
        >
          <Heading type={Heading.types.H2}>
            {dialogTitle}
          </Heading>
          <button
            className="rp-report-config__close"
            onClick={handleCancel}
            aria-label="Close"
          >
            &times;
          </button>
        </Flex>

        {/* Period */}
        <div className="rp-report-config__field">
          <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
            Period
          </Text>
          <Dropdown
            size={Dropdown.sizes.MEDIUM}
            options={activePeriodOptions}
            value={selectedPeriodValue}
            onChange={(option: { value: string } | null) => {
              if (option) updateConfig({ period: option.value as ReportPeriod });
            }}
            clearable={false}
            placeholder="Select period"
          />
        </div>

        {/* Scope — hidden for worklog and planned vs actual reports */}
        {!isWorklogReport && !isPlannedVsActual && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              Scope
            </Text>
            <Dropdown
              size={Dropdown.sizes.MEDIUM}
              options={SCOPE_OPTIONS}
              value={selectedScopeValue}
              onChange={(option: { value: string } | null) => {
                if (option) updateConfig({ scope: option.value as ReportScope });
              }}
              clearable={false}
              placeholder="Select scope"
            />
          </div>
        )}

        {/* Custom date range */}
        {currentConfig.period === 'custom' && (
          <Flex gap={Flex.gaps.SMALL}>
            <div className="rp-report-config__field" style={{ flex: 1 }}>
              <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
                Start Date
              </Text>
              <input
                type="date"
                className="rp-report-config__input"
                value={currentConfig.customStartDate ?? ''}
                onChange={(e) =>
                  updateConfig({ customStartDate: e.target.value })
                }
              />
            </div>
            <div className="rp-report-config__field" style={{ flex: 1 }}>
              <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
                End Date
              </Text>
              <input
                type="date"
                className="rp-report-config__input"
                value={currentConfig.customEndDate ?? ''}
                onChange={(e) =>
                  updateConfig({ customEndDate: e.target.value })
                }
              />
            </div>
          </Flex>
        )}

        {/* Teams — for utilization, leave, and planned vs actual reports (not board resources) */}
        {!isBoardResources && teamOptions.length > 0 && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              Teams
            </Text>
            <Dropdown
              size={Dropdown.sizes.MEDIUM}
              multi
              multiline
              searchable
              options={teamOptions}
              value={selectedTeamValues}
              onChange={(options: Array<{ value: string }> | null) => {
                updateConfig({ teamIds: (options ?? []).map((o) => o.value) });
              }}
              onOptionRemove={(option: { value: string }) => {
                const current = currentConfig.teamIds ?? [];
                updateConfig({ teamIds: current.filter((t) => t !== option.value) });
              }}
              closeMenuOnSelect={false}
              clearable
              placeholder="Select teams..."
            />
          </div>
        )}

        {/* People — for utilization, leave, and planned vs actual reports (not board resources) */}
        {!isBoardResources && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              People
            </Text>
            <Dropdown
              size={Dropdown.sizes.MEDIUM}
              multi
              multiline
              searchable
              options={userOptions}
              value={selectedUserValues}
              onChange={(options: Array<{ value: string }> | null) => {
                updateConfig({ userIds: (options ?? []).map((o) => o.value) });
              }}
              onOptionRemove={(option: { value: string }) => {
                const current = currentConfig.userIds ?? [];
                updateConfig({ userIds: current.filter((u) => u !== option.value) });
              }}
              closeMenuOnSelect={false}
              clearable
              placeholder="All people"
            />
          </div>
        )}

        {/* Boards — not applicable for leave or worklog report */}
        {!isLeaveReport && !isWorklogReport && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              Boards
            </Text>
            <MultiBoardPicker
              value={selectedBoardsPicked}
              onChange={(boards) => {
                setSelectedBoardsPicked(boards);
                updateConfig({ boardIds: boards.map((b) => b.id) });
              }}
              placeholder="Search and select boards..."
            />
            {selectedBoardsPicked.length === 0 && !isPlannedVsActual && (
              <Text
                type={Text.types.TEXT2}
                style={{ color: 'var(--secondary-text-color, #676879)', marginTop: 4, fontSize: 12 }}
              >
                Leave empty to include all configured boards (max {MAX_REPORT_BOARDS} newest)
              </Text>
            )}
            {selectedBoardsPicked.length === 0 && isPlannedVsActual && (
              <Text
                type={Text.types.TEXT2}
                style={{ color: '#d83a52', marginTop: 4, fontSize: 12 }}
              >
                At least one board is required
              </Text>
            )}
          </div>
        )}

        {/* View mode — only for Planned vs Actual */}
        {isPlannedVsActual && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              View
            </Text>
            <Dropdown
              size={Dropdown.sizes.MEDIUM}
              options={[
                { value: 'item', label: 'By Item' },
                { value: 'board', label: 'By Board' },
              ]}
              value={
                currentConfig?.groupBy === 'board'
                  ? { value: 'board', label: 'By Board' }
                  : { value: 'item', label: 'By Item' }
              }
              onChange={(option: { value: string } | null) => {
                if (option) {
                  updateConfig({
                    groupBy: option.value as 'item' | 'board',
                  });
                }
              }}
              clearable={false}
              placeholder="Select view"
            />
          </div>
        )}

        {/* Group by — for utilization and worklog reports */}
        {!isBoardResources && !isLeaveReport && !isPlannedVsActual && (
          <div className="rp-report-config__field">
            <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
              Group by
            </Text>
            <Dropdown
              size={Dropdown.sizes.MEDIUM}
              options={groupByOptions}
              value={selectedGroupByValue}
              onChange={(option: { value: string } | null) => {
                if (option) {
                  updateConfig({
                    groupBy: option.value as 'none' | 'board' | 'item' | 'team' | 'category',
                  });
                }
              }}
              clearable={false}
              placeholder="Group by"
            />
          </div>
        )}

        {/* Error message */}
        {reportError && !generating && (
          <Text
            type={Text.types.TEXT2}
            style={{ color: '#d83a52', marginTop: 8 }}
          >
            {reportError}
          </Text>
        )}

        {/* Actions */}
        <Flex
          gap={Flex.gaps.SMALL}
          justify={Flex.justify.END}
          style={{ marginTop: 24 }}
        >
          <Button
            kind={Button.kinds.PRIMARY}
            onClick={handleGenerate}
            loading={generating}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Create'}
          </Button>
          <Button kind={Button.kinds.TERTIARY} onClick={handleCancel} disabled={generating}>
            Cancel
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default ReportConfigDialog;
